import {
  computed,
  inject,
  provide,
  shallowRef,
  type Component,
  type ComputedRef,
  type InjectionKey,
  type Ref,
  type ShallowRef
} from 'vue'
import { parseModalsParam, serializeModals } from './modals-codec'

/**
 * Central modal-stack contract (central-modal-host plan, Phase 1). One ordered
 * stack owns which overlays are open and in what order; z-index, the tabs
 * strip, and URL persistence are all projections of this single array. See
 * `docs/plans/in-progress/central-modal-host.md`.
 */

// --- z-index bands (plan 0.4) -------------------------------------------------

/**
 * z of the bottom-most stacked modal (entry index 0). A stacked modal i takes
 * `MODAL_STACK_BASE + i`. Base 51 keeps the bottom modal exactly where the
 * legacy allocator put the first-opened dialog (globalZIndex 50 → 51), so
 * nothing layered against page chrome (`z-50`) moves. Transient overlays
 * (confirms/popovers opened from within a modal) float dynamically above the
 * current stack top via `ModalStack.topZ` (see `useDialogZIndex`), so they are
 * always on top without a fixed reserved ceiling.
 */
export const MODAL_STACK_BASE = 51

// --- entries + registry -------------------------------------------------------

/** Serializable params for a persistent modal kind (URL-backed). */
export type ModalParams = Record<string, string>

export interface ModalStackEntry {
  /**
   * Identity within the stack. Each kind is a singleton (you never stack two
   * stakeholder-detail modals), so `id === kind`. Keying the host by this gives
   * stable component instances across param changes — no remount, no flicker.
   */
  id: string
  kind: string
  params: ModalParams
}

export interface ModalRegistration {
  /** The component the host renders for this kind; receives `params` as props. */
  component: Component
  /**
   * `true` → serializable params, URL-backed, restored on refresh.
   * `false` → in-memory only (callbacks allowed), never serialized (Phase 6).
   */
  persist: boolean
  /** Human label for the tabs strip (Phase 3). */
  label?: string
  /** params → payload for the `?modals=` codec (Phase 2). */
  toQuery?: (params: ModalParams) => string
  /** payload → params for the `?modals=` codec (Phase 2). */
  fromQuery?: (payload: string) => ModalParams
  /**
   * Non-modal query params this kind owns (e.g. a panel sub-scope). When the
   * kind closes, the codec strips these in the same `?modals=` write, so a
   * sub-scope never lingers without its modal.
   */
  cleanupQueryKeys?: string[]
}

const registry = new Map<string, ModalRegistration>()

/** Register a kind → component (+ metadata). Called once per kind at startup. */
export function registerModal(
  kind: string,
  registration: ModalRegistration
): void {
  registry.set(kind, registration)
}

export function getModalRegistration(
  kind: string
): ModalRegistration | undefined {
  return registry.get(kind)
}

export function getRegisteredModalKinds(): string[] {
  return [...registry.keys()]
}

/** Test-only: drop all registrations so suites don't leak into each other. */
export function clearModalRegistry(): void {
  registry.clear()
}

/**
 * Ephemeral (`persist: false`) kinds participate in the stack/tabs/z-order but
 * are never written to or read from `?modals=` (Phase 6.2). Unregistered kinds
 * are treated as persistent (the host just won't render them).
 */
function isPersistentKind(kind: string): boolean {
  return registry.get(kind)?.persist !== false
}

/** Serialize the persistent entries of a stack to a `?modals=` value. */
export function stackToModalsParam(entries: ModalStackEntry[]): string {
  return serializeModals(
    entries
      .filter((entry) => isPersistentKind(entry.kind))
      .map((entry) => {
        const registration = registry.get(entry.kind)
        return {
          kind: entry.kind,
          payload: registration?.toQuery
            ? registration.toQuery(entry.params)
            : undefined
        }
      })
  )
}

/** Parse a `?modals=` value into stack entries (persistent kinds only). */
export function modalsParamToStackEntries(
  modals: string | null | undefined
): { kind: string; params: ModalParams }[] {
  return parseModalsParam(modals)
    .filter((entry) => isPersistentKind(entry.kind))
    .map((entry) => {
      const registration = registry.get(entry.kind)
      const params: ModalParams =
        registration?.fromQuery && entry.payload != null
          ? registration.fromQuery(entry.payload)
          : {}
      return { kind: entry.kind, params }
    })
}

// --- store --------------------------------------------------------------------

export interface ModalStack {
  /** The ordered stack, bottom→top. Single owner of order. */
  entries: Readonly<ShallowRef<ModalStackEntry[]>>
  /** z of the current top entry, or the band floor when empty. */
  topZ: ComputedRef<number>
  /** Open a kind on top; re-opening an existing kind moves it to the top. */
  open: (kind: string, params?: ModalParams) => void
  /** Move an already-open kind to the top (focus = move-to-end). */
  raise: (kind: string) => void
  /** Replace an open kind's params in place (no reorder). No-op if not open. */
  update: (kind: string, params: ModalParams) => void
  /** Close a kind. */
  close: (kind: string) => void
  /** Close every entry. */
  closeAll: () => void
  /** Replace the whole stack in one shot, in array order (bridge/codec). */
  set: (entries: { kind: string; params?: ModalParams }[]) => void
}

export function createModalStack(): ModalStack {
  // shallowRef holds plain entry/param objects rather than deep-reactive
  // proxies. Identity is matched by the `kind` STRING (each kind is a
  // singleton), so — unlike the single-object section-menu registry — this
  // needs no `toRaw` guard.
  const entries = shallowRef<ModalStackEntry[]>([])

  const topZ = computed(() =>
    entries.value.length > 0
      ? MODAL_STACK_BASE + entries.value.length - 1
      : MODAL_STACK_BASE - 1
  )

  function withoutKind(kind: string): ModalStackEntry[] {
    return entries.value.filter((entry) => entry.kind !== kind)
  }

  function open(kind: string, params: ModalParams = {}): void {
    entries.value = [...withoutKind(kind), { id: kind, kind, params }]
  }

  function raise(kind: string): void {
    const list = entries.value
    const existing = list.find((entry) => entry.kind === kind)
    if (!existing || list[list.length - 1]?.kind === kind) return
    entries.value = [...withoutKind(kind), existing]
  }

  function update(kind: string, params: ModalParams): void {
    const index = entries.value.findIndex((entry) => entry.kind === kind)
    if (index < 0) return
    const next = [...entries.value]
    next[index] = { id: kind, kind, params }
    entries.value = next
  }

  function close(kind: string): void {
    if (!entries.value.some((entry) => entry.kind === kind)) return
    entries.value = withoutKind(kind)
  }

  function closeAll(): void {
    if (entries.value.length === 0) return
    entries.value = []
  }

  function set(next: { kind: string; params?: ModalParams }[]): void {
    entries.value = next.map((entry) => ({
      id: entry.kind,
      kind: entry.kind,
      params: entry.params ?? {}
    }))
  }

  return { entries, topZ, open, raise, update, close, closeAll, set }
}

// --- injection ----------------------------------------------------------------

export const modalStackKey: InjectionKey<ModalStack> = Symbol('gs-modal-stack')

/** Per-entry z provided by `ModalStackHost` around each rendered entry. */
export const modalStackEntryZKey: InjectionKey<Readonly<Ref<number>>> = Symbol(
  'gs-modal-stack-entry-z'
)

/**
 * Provided by `ModalStackSlot` around each rendered entry: a ref of whether
 * that entry is the current top of the stack. A non-null inject also marks the
 * consuming modal as a *direct* host-stack entry (see `useModalStackEntry`),
 * which makes it render **non-modal** so the central stack — not reka's
 * last-mounted-wins layering — owns which overlay is interactive. The entry
 * re-provides `null` to its own subtree, so a confirm/picker opened *inside* a
 * stacked modal is an ordinary modal dialog again.
 */
export const modalStackEntryTopKey: InjectionKey<Readonly<
  Ref<boolean>
> | null> = Symbol('gs-modal-stack-entry-top')

/** Provide the stack at the app root (call inside a setup context). */
export function provideModalStack(stack: ModalStack): void {
  provide(modalStackKey, stack)
}

export function useModalStack(): ModalStack {
  const stack = inject(modalStackKey)
  if (!stack) {
    throw new Error(
      'useModalStack() called with no ModalStack provided — call provideModalStack() at the app root.'
    )
  }
  return stack
}

/**
 * Read the host-stack context for the modal currently being rendered, and
 * isolate descendants from it.
 *
 * `isHostStackEntry` is true when this modal is a direct entry rendered by
 * `<ModalStackHost>` (vs a standalone modal, or a confirm nested inside one).
 * Host entries render **non-modal**: the central stack owns stacking/interaction
 * order, while the dim backdrop, page-block, and scroll-lock all come from the
 * *top* entry's overlay (which still mounts reka's `DialogOverlay`). `isTop` is
 * true for the entry currently at the top of the stack.
 *
 * Call once per modal in `ModalDialog`/`ModalDrawer`. It re-provides a cleared
 * context to its own subtree so a nested confirm/picker is an ordinary modal.
 */
export function useModalStackEntry(): {
  isHostStackEntry: boolean
  isTop: ComputedRef<boolean>
} {
  const topRef = inject(modalStackEntryTopKey, null)
  provide(modalStackEntryTopKey, null)
  return {
    isHostStackEntry: topRef != null,
    isTop: computed(() => topRef?.value ?? false)
  }
}
